/**
 * Lớp dữ liệu của trang giới thiệu quán.
 *
 * Đây là chỗ duy nhất biết dữ liệu đến từ đâu. Backend trả đúng hình dạng trong
 * interfaces/venue.ts nên không còn phải đổi tên field gì; helpers/venueApi.ts
 * chỉ còn kiểm tra payload có đủ để vẽ hay không.
 */
import { useCallback, useEffect, useState } from "react";
import * as SBService from "../helpers/sportbookService";
import { toFeaturedVenues, toVenuePage } from "../helpers/venueApi";
import { FeaturedVenue, VenuePage } from "../interfaces/venue";

export interface VenuePageState {
  data: VenuePage | null;
  loading: boolean;
  failed: boolean;
  reload: () => void;
}

export const useVenuePage = (venueId: string): VenuePageState => {
  const [data, setData] = useState<VenuePage | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    // Không có id thì không gọi API: `/public/venues/` là một đường dẫn khác hẳn,
    // và màn hình phải ra trạng thái lỗi chứ không phải quay mãi.
    if (!venueId) {
      setData(null);
      setFailed(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setFailed(false);
    try {
      const response = await SBService.getPublicVenueById(venueId);
      const page = toVenuePage(response?.data ?? response);
      // Quán tắt hoặc không có -> backend trả 404, callApi nuốt lỗi và trả về
      // thứ không map được. Không phân biệt với lỗi mạng: với người xem thì cả
      // hai đều là "không mở được trang này", và màn lỗi đã có nút thử lại.
      if (!page) {
        throw new Error("Không đọc được dữ liệu quán");
      }
      setData(page);
    } catch (e) {
      setData(null);
      setFailed(true);
    }
    setLoading(false);
  }, [venueId]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, failed, reload: load };
};

/**
 * Quán nổi bật cho trang chủ.
 *
 * Hỏng thì trả mảng rỗng chứ không ném lỗi: đây là một khối phụ của trang chủ,
 * không được phép kéo cả trang xuống theo. Trang chủ tự ẩn khối khi rỗng.
 */
export const useFeaturedVenues = (limit = 6): FeaturedVenue[] => {
  const [venues, setVenues] = useState<FeaturedVenue[]>([]);

  useEffect(() => {
    let cancelled = false;
    SBService.getFeaturedVenues(limit)
      .then((response: any) => {
        if (!cancelled) {
          setVenues(toFeaturedVenues(response?.data ?? response));
        }
      })
      .catch((error: unknown) => {
        // eslint-disable-next-line no-console
        console.error("Không tải được danh sách quán nổi bật:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [limit]);

  return venues;
};

export interface VenueListState {
  venues: FeaturedVenue[];
  loading: boolean;
  failed: boolean;
  hasNextPage: boolean;
  loadMore: () => void;
}

/**
 * Danh sách quán của trang /venue.
 *
 * Cuộn thêm chứ không phân trang bằng số: trang chạy trong vỏ 430px trên điện
 * thoại, một dãy số trang ở đó vừa khó bấm vừa thừa.
 *
 * Đổi từ khoá là về trang 1 và THAY danh sách, không nối tiếp - nếu không thì kết
 * quả tìm mới chồng lên kết quả tìm cũ.
 */
export const useVenueList = (keyword: string, limit = 12): VenueListState => {
  const [venues, setVenues] = useState<FeaturedVenue[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);

  /* Từ khoá và số trang là MỘT state, không phải hai.
     Tách ra hai state rồi dùng một effect để đặt lại trang thì effect tải vẫn kịp
     chạy một lượt với số trang cũ: đang ở trang 3 mà gõ từ khoá mới là bắn đi một
     lời gọi `page=3&keyword=<mới>` rồi vứt kết quả. Gộp lại thì mỗi lần đổi chỉ
     còn đúng một lời gọi. */
  const [oLoc, setOLoc] = useState({ keyword, page: 1 });

  if (oLoc.keyword !== keyword) {
    // Đặt state ngay trong thân hàm khi đạo cụ đổi: React bỏ kết quả render này và
    // render lại ngay, nên không có lượt nào chạy với cặp (từ khoá mới, trang cũ).
    setOLoc({ keyword, page: 1 });
  }

  const { page } = oLoc;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    SBService.getPublicVenues({ page, limit, keyword: keyword || undefined })
      .then((response: any) => {
        if (cancelled) {
          return;
        }
        const them = toFeaturedVenues(response?.data);
        setVenues((truoc) => (page === 1 ? them : [...truoc, ...them]));
        setHasNextPage(response?.hasNextPage === true);
        setFailed(false);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        // Trang 1 hỏng là không có gì để xem -> màn lỗi. Trang sau hỏng thì những
        // quán đã hiện vẫn còn đó, chỉ là không cuộn thêm được.
        if (page === 1) {
          setVenues([]);
          setFailed(true);
        }
        setHasNextPage(false);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [keyword, page, limit]);

  return {
    venues,
    loading,
    failed,
    hasNextPage,
    loadMore: () => setOLoc((truoc) => ({ ...truoc, page: truoc.page + 1 })),
  };
};
